import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppTopBar } from "@/components/AppTopBar";
import { formatIDR } from "@/components/AppTopBar";

const canView = (role) => role === "OWNER" || role === "ADMIN_INVENTORY";

export default function InventoryList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [sel, setSel] = useState({}); // product_id -> bool (eligible source SKUs only)
  const [q, setQ] = useState("");
  const [ind, setInd] = useState("ALL"); // summary indicator / status filter
  const [sort, setSort] = useState("SKU");
  const isOwner = user?.role === "OWNER";

  const load = useCallback(() => {
    api.get("/inventory").then(({ data }) => setItems(data))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateBuffer = async (it) => {
    const val = window.prompt(`Stock Buffer (PCS) untuk ${it.sku_code} — ditahan dari distribusi, tidak mengubah stok fisik`, String(it.stock_buffer));
    if (val === null) return;
    setError("");
    try { await api.patch(`/inventory/${it.product_id}/buffer`, { stock_buffer: parseInt(val, 10) }); load(); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const bulkUpdate = async () => {
    const ids = Object.keys(sel).filter((k) => sel[k]);
    if (ids.length === 0) { setError("Pilih minimal satu SKU sumber untuk bulk buffer."); return; }
    const val = window.prompt(`Bulk Stock Buffer (PCS) untuk ${ids.length} SKU terpilih`, "0");
    if (val === null) return;
    setError("");
    try { await api.post(`/inventory/buffer/bulk`, { product_ids: ids, stock_buffer: parseInt(val, 10) }); setSel({}); load(); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const toggle = (pid) => setSel((s) => ({ ...s, [pid]: !s[pid] }));
  const distAvail = (it) => it.distribution_available ?? 0;
  const matchInd = (it) => {
    if (ind === "READY") return distAvail(it) > 0;
    if (ind === "HABIS") return distAvail(it) <= 0;
    if (ind === "MENIPIS") return distAvail(it) > 0 && distAvail(it) <= 10;
    if (ind === "BUFFER") return (it.stock_buffer || 0) > 0;
    if (ind === "KOMPONEN") return it.stock_source === "COMPONENT";
    if (ind === "SINGLE") return it.stock_source === "SINGLE";
    return true;
  };
  const counts = {
    ALL: items.length,
    READY: items.filter((it) => distAvail(it) > 0).length,
    HABIS: items.filter((it) => distAvail(it) <= 0).length,
    MENIPIS: items.filter((it) => distAvail(it) > 0 && distAvail(it) <= 10).length,
    BUFFER: items.filter((it) => (it.stock_buffer || 0) > 0).length,
    KOMPONEN: items.filter((it) => it.stock_source === "COMPONENT").length,
  };

  if (user && !canView(user.role)) {
    return (<div className="app-shell"><AppTopBar active="inventory" /><main className="content"><div className="alert-error" data-testid="access-denied">Akses ditolak.</div></main></div>);
  }

  const selCount = Object.values(sel).filter(Boolean).length;
  const colSpan = (isOwner ? 8 : 6) + 2;
  const term = q.trim().toLowerCase();
  let shown = items.filter((it) => matchInd(it) &&
    (!term || (it.sku_code || "").toLowerCase().includes(term) || (it.description || "").toLowerCase().includes(term)));
  const cmp = {
    SKU: (a, b) => (a.sku_code || "").localeCompare(b.sku_code || ""),
    AVAIL_DESC: (a, b) => (b.sellable_available ?? b.available_qty) - (a.sellable_available ?? a.available_qty),
    AVAIL_ASC: (a, b) => (a.sellable_available ?? a.available_qty) - (b.sellable_available ?? b.available_qty),
    DIST_DESC: (a, b) => distAvail(b) - distAvail(a),
    DIST_ASC: (a, b) => distAvail(a) - distAvail(b),
    BUFFER_DESC: (a, b) => (b.stock_buffer || 0) - (a.stock_buffer || 0),
  };
  shown = [...shown].sort(cmp[sort] || cmp.SKU);
  const eligibleVisible = shown.filter((it) => it.buffer_eligible);
  const allVisSelected = eligibleVisible.length > 0 && eligibleVisible.every((it) => sel[it.product_id]);
  const toggleAllVisible = () => {
    setSel((s) => {
      const next = { ...s };
      const target = !allVisSelected;
      eligibleVisible.forEach((it) => { next[it.product_id] = target; });
      return next;
    });
  };
  const INDS = [["ALL", "Semua"], ["READY", "Ready"], ["MENIPIS", "Menipis ≤10"], ["HABIS", "Habis"], ["BUFFER", "Buffer"], ["KOMPONEN", "Komponen"]];

  return (
    <div className="app-shell">
      <AppTopBar active="inventory" />
      <main className="content" style={{ maxWidth: 1600 }}>
        {error && <div className="alert-error" data-testid="error-banner">{error}</div>}
        <section className="panel" data-testid="inventory-panel">
          <h2 className="panel-title">Inventory ({items.length})</h2>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "4px 0 10px" }}>
            {INDS.map(([k, label]) => (
              <button key={k} onClick={() => setInd(k)} data-testid={`ind-${k}`}
                className={`status-pill ${ind === k ? "on" : "off"}`}
                style={{ cursor: "pointer", border: ind === k ? "1px solid var(--accent)" : "1px solid var(--border)", padding: "4px 10px" }}>
                {label} {counts[k] ?? 0}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", margin: "0 0 8px" }}>
            <input className="field-input" style={{ maxWidth: 300 }} placeholder="Cari SKU / Deskripsi..." value={q}
              onChange={(e) => setQ(e.target.value)} data-testid="inventory-search" />
            <select className="field-input" style={{ width: "auto" }} value={ind} onChange={(e) => setInd(e.target.value)} data-testid="inventory-status-filter">
              <option value="ALL">Status: Semua</option>
              <option value="READY">Ada Stok</option>
              <option value="HABIS">Stok 0</option>
              <option value="MENIPIS">Stok Menipis (≤10)</option>
              <option value="BUFFER">Buffer &gt; 0</option>
              <option value="SINGLE">Single</option>
              <option value="KOMPONEN">Komponen</option>
            </select>
            <select className="field-input" style={{ width: "auto" }} value={sort} onChange={(e) => setSort(e.target.value)} data-testid="inventory-sort">
              <option value="SKU">Urutkan: SKU A-Z</option>
              <option value="AVAIL_DESC">Available Terbesar</option>
              <option value="AVAIL_ASC">Available Terkecil</option>
              <option value="DIST_DESC">Distribusi Terbesar</option>
              <option value="DIST_ASC">Distribusi Terkecil</option>
              <option value="BUFFER_DESC">Buffer Terbesar</option>
            </select>
            <button className="btn-primary" style={{ width: "auto", flex: "0 0 auto" }} onClick={bulkUpdate} disabled={selCount === 0} data-testid="bulk-buffer-button">
              Bulk Update Buffer{selCount ? ` (${selCount})` : ""}
            </button>
          </div>
          <p className="hint">Available = operasional (SINGLE: GOOD fisik; KOMPONEN: dari stok komponen). Distribusi = Available setelah dikurangi Stock Buffer sumber. Buffer tidak mengubah stok fisik.</p>
          <div className="table-scroll">
            <table className="data-table" data-testid="inventory-table">
              <thead>
                <tr>
                  <th><input type="checkbox" checked={allVisSelected} onChange={toggleAllVisible} data-testid="select-all" title="Pilih semua SKU sumber yang terlihat" /></th>
                  <th>SKU</th>
                  <th>Deskripsi</th>
                  <th>Sumber</th>
                  <th>Status</th>
                  <th>Available</th>
                  <th>Buffer</th>
                  <th>Distribusi</th>
                  <th>Damaged</th>
                  {isOwner && <th>Avg Cost</th>}
                  {isOwner && <th>Nilai GOOD</th>}
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && <tr><td colSpan={colSpan + 1} className="empty-cell">Belum ada SKU.</td></tr>}
                {items.length > 0 && shown.length === 0 && <tr><td colSpan={colSpan + 1} className="empty-cell">Tidak ada SKU cocok dengan filter/pencarian.</td></tr>}
                {shown.map((it) => (
                  <tr key={it.product_id} data-testid={`inventory-row-${it.sku_code}`}>
                    <td>{it.buffer_eligible && <input type="checkbox" checked={!!sel[it.product_id]} onChange={() => toggle(it.product_id)} data-testid={`select-${it.sku_code}`} />}</td>
                    <td className="mono">{it.sku_code}</td>
                    <td>{it.description || "-"}</td>
                    <td data-testid={`source-${it.sku_code}`}>
                      <span className={`status-pill ${it.stock_source === "COMPONENT" ? "on" : "off"}`}>
                        {it.stock_source === "COMPONENT" ? "Komponen" : "Single"}
                      </span>
                    </td>
                    <td data-testid={`status-${it.sku_code}`}>
                      <span className={`status-pill ${distAvail(it) > 0 ? "on" : "off"}`}>{distAvail(it) > 0 ? "READY" : "HABIS"}</span>
                    </td>
                    <td className="mono" data-testid={`available-${it.sku_code}`}>
                      {it.stock_source === "COMPONENT" ? (it.sellable_available ?? 0) : it.available_qty}
                      {it.stock_source === "COMPONENT" && <span className="hint" style={{ display: "block" }}>derived (fisik {it.available_qty})</span>}
                    </td>
                    <td className="mono" data-testid={`buffer-${it.sku_code}`}>{it.buffer_eligible ? it.stock_buffer : "-"}</td>
                    <td className="mono" data-testid={`distribution-${it.sku_code}`}>{it.distribution_available}</td>
                    <td className="mono" data-testid={`damaged-${it.sku_code}`}>{it.damaged_qty}</td>
                    {isOwner && <td className="mono" data-testid={`avg-${it.sku_code}`}>{it.good_avg_cost != null ? <>{formatIDR(it.good_avg_cost)}{it.cost_is_derived && <span className="tag-warn" style={{ marginLeft: 6 }} data-testid={`avg-derived-${it.sku_code}`}>derivatif</span>}</> : (it.hpp_reference != null ? <span data-testid={`hpp-ref-${it.sku_code}`}>{formatIDR(it.hpp_reference)}<span className="hint" style={{ display: "block" }}>HPP Referensi</span></span> : "belum diinisiasi")}</td>}
                    {isOwner && <td className="mono" data-testid={`value-${it.sku_code}`}>{it.cost_status === "UNINITIALIZED" ? "-" : formatIDR(it.good_value || 0)}</td>}
                    <td className="actions-cell">
                      <button className="btn-mini" onClick={() => navigate(`/inventory/${it.product_id}`)} data-testid={`detail-${it.sku_code}`}>Detail</button>
                      {it.buffer_eligible && <button className="btn-mini" onClick={() => updateBuffer(it)} data-testid={`buffer-btn-${it.sku_code}`}>Buffer</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
